/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { UiIcon } from '../common/icons/ui-icon'
import { concatCssClasses } from '../../utils/concat-css-classes'
import { getNoteLabel } from './build-note-tree'
import styles from './note-tree-sidebar.module.scss'
import { NoteType, type NoteTreeEntryInterface } from '@hedgedoc/commons'
import Link from 'next/link'
import type { ContextMenuPosition } from './note-tree-context-menu'
import React, { useCallback } from 'react'
import { Easel as IconSlide, FileEarmarkText as IconDocument } from 'react-bootstrap-icons'
import { buildNoteUrl } from '../../utils/note-url'

export interface NoteTreeNoteItemProps {
  note: NoteTreeEntryInterface
  depth: number
  active: boolean
  onContextMenu: (note: NoteTreeEntryInterface, position: ContextMenuPosition) => void
}

/**
 * Renders a link to a note in the navigation tree.
 */
export const NoteTreeNoteItem: React.FC<NoteTreeNoteItemProps> = ({ note, depth, active, onContextMenu }) => {
  const label = getNoteLabel(note)
  const onLinkContextMenu = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()
      // Context menus opened with the keyboard (shift+F10 or the menu key) have no pointer position
      if (event.clientX === 0 && event.clientY === 0) {
        const rect = event.currentTarget.getBoundingClientRect()
        onContextMenu(note, { x: rect.left, y: rect.bottom })
      } else {
        onContextMenu(note, { x: event.clientX, y: event.clientY })
      }
    },
    [note, onContextMenu]
  )
  return (
    <li>
      <Link
        href={buildNoteUrl(note.primaryAlias)}
        className={concatCssClasses(styles.row, styles.note, { [styles.active]: active })}
        style={{ paddingLeft: `${depth * 12 + 20}px` }}
        title={note.title ? `${note.title} (${note.primaryAlias})` : note.primaryAlias}
        aria-current={active ? 'page' : undefined}
        onContextMenu={onLinkContextMenu}>
        {note.badge ? (
          <span className={concatCssClasses(styles['row-icon'], styles.badge)} aria-hidden={true}>
            {note.badge}
          </span>
        ) : (
          <UiIcon icon={note.type === NoteType.SLIDE ? IconSlide : IconDocument} className={styles['row-icon']} />
        )}
        <span className={styles.label}>{label}</span>
      </Link>
    </li>
  )
}
